// frontend/src/context/AuthContext.jsx
import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // App ачаалагдахад token шалгах
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedPermissions = localStorage.getItem('permissions');

    if (storedToken && storedUser) {
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('User parse error:', e);
        setUser(null);
      }
      
      if (storedPermissions) {
        try {
          const parsed = JSON.parse(storedPermissions);
          // Array эсэхийг шалгах
          setPermissions(Array.isArray(parsed) ? parsed : []);
        } catch (e) {
          console.error('Permissions parse error:', e);
          setPermissions([]);
        }
      }
    }
    setLoading(false);
  }, []);

  // Login function
  const login = async (username, password) => {
    try {
      const response = await authAPI.smartLogin({
        username,
        password,
      });

      const { token: newToken, user: newUser } = response.data;
      const userPermissions = newUser.permissions || [];

      // LocalStorage-д хадгалах
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('permissions', JSON.stringify(userPermissions));

      // State шинэчлэх
      setToken(newToken);
      setUser(newUser);
      setPermissions(userPermissions);

      return { success: true, user: newUser };
    } catch (error) {
      const message = error.response?.data?.message || 'Нэвтрэхэд алдаа гарлаа';
      return { success: false, message };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    setToken(null);
    setUser(null);
    setPermissions([]);
  };

  // Register function
  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token: newToken, user: newUser } = response.data;

      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));

      setToken(newToken);
      setUser(newUser);

      return { success: true, user: newUser };
    } catch (error) {
      const message = error.response?.data?.message || 'Бүртгэлд алдаа гарлаа';
      return { success: false, message };
    }
  };

  // ============================================
  // PERMISSION HELPER FUNCTIONS
  // ============================================

  /**
   * Нэг permission шалгах
   * @param {string} permission - Шалгах эрхийн key (e.g., 'user.view')
   */
  const hasPermission = (permission) => {
    // System Admin: Super admin бол бүх эрхтэй
    if (user?.user_type === 'system_admin' && user?.is_super_admin) {
      return true;
    }
    
    // Company User: is_admin бол бүх эрхтэй
    if (user?.user_type === 'company_user' && user?.is_admin) {
      return true;
    }

    // Null check нэмсэн
    if (!Array.isArray(permissions)) {
      return false;
    }

    return permissions.includes(permission);
  };

  /**
   * Олон permission-ийн аль нэгийг шалгах
   * @param {string[]} permissionList - Шалгах эрхүүдийн array
   */
   const hasAnyPermission = (permissionList) => {
    if (user?.user_type === 'system_admin' && user?.is_super_admin) {
      return true;
    }
    
    if (user?.user_type === 'company_user' && user?.is_admin) {
      return true;
    }

    // Null check нэмсэн
    if (!Array.isArray(permissions) || !Array.isArray(permissionList)) {
      return false;
    }

    return permissionList.some(p => permissions.includes(p));
  };
  /**
   * Бүх permission-үүд байгаа эсэх шалгах
   * @param {string[]} permissionList - Шалгах эрхүүдийн array
   */
  const hasAllPermissions = (permissionList) => {
    if (user?.user_type === 'system_admin' && user?.is_super_admin) {
      return true;
    }
    
    if (user?.user_type === 'company_user' && user?.is_admin) {
      return true;
    }

    // Null check нэмсэн
    if (!Array.isArray(permissions) || !Array.isArray(permissionList)) {
      return false;
    }

    return permissionList.every(p => permissions.includes(p));
  };

  /**
   * Admin эсэх шалгах (System эсвэл Company)
   */
  const isAdmin = () => {
    if (user?.user_type === 'system_admin') {
      return user?.is_super_admin || false;
    }
    if (user?.user_type === 'company_user') {
      return user?.is_admin || false;
    }
    return false;
  };

  const value = {
    user,
    token,
    permissions,
    loading,
    login,
    logout,
    register,
    // Permission helpers
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};